import React from 'react';
import type { LucideProps } from 'lucide-react';
import { ICONS, type IconKey } from './registry';

export interface IconProps extends Omit<LucideProps, 'ref' | 'size' | 'name'> {
  name: IconKey;
  /** Explicit pixel size. Omit to inherit the parent's font-size (renders at 1em). */
  size?: number | string;
  /** Solid glyph, for status dots and "recording" indicators. */
  filled?: boolean;
}

/**
 * The single render path for icons.
 *
 * Sizing defaults to `1em` rather than lucide's 24px so that the existing `font-size` rules in
 * App.css keep controlling icon size — including the `transition: font-size` hover-grow on the
 * aircraft and webcam markers. lucide's `Icon` spreads `...rest` *after* its own `width`/`height`
 * (see node_modules/lucide-react/dist/esm/Icon.mjs), so these win.
 *
 * Color is never set here: lucide emits `stroke="currentColor"`, so every icon inherits its
 * container's color. That is what keeps icons monochrome by default while letting the surfaces
 * that already carry meaning — incident type, unit status, point color — tint them for free.
 */
export function Icon({ name, size, filled, className, strokeWidth = 1.75, ...rest }: IconProps) {
  const Cmp = ICONS[name] ?? ICONS.unknown;
  return (
    <Cmp
      strokeWidth={strokeWidth}
      focusable="false"
      className={className ? `app-icon ${className}` : 'app-icon'}
      {...(filled ? { fill: 'currentColor' } : null)}
      {...(size != null ? { size } : { width: '1em', height: '1em' })}
      {...rest}
    />
  );
}

export default Icon;
